import React, { Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import Login from './components/Login';
import PrivateRoute from './components/PrivateRoute';
import Layout from './components/Layout';
import './i18n';
import './index.css';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'; // Import des styles de Toastify
import 'animate.css'; 
import ProtectedRoute from './components/auth/ProtectedRoute';
import './utils/debugUser'; // Utilitaires de debug

// Chargement à la demande : sans cela, les bibliothèques de génération de PDF,
// d'Excel et de graphiques — 62 % du bundle — étaient téléchargées et
// interprétées dès la page de connexion, où elles ne servent à rien.
const Register = React.lazy(() => import('./components/Register'));
const Dashboard = React.lazy(() => import('./components/Dashboard'));
const SchoolSchedule = React.lazy(() => import('./components/SchoolSchedule'));
const Settings = React.lazy(() => import('./components/Settings'));
const FeesPage = React.lazy(() => import('./components/FeesPage'));
const StatisticsPage = React.lazy(() => import('./components/StatisticsPage'));
const ClassForm = React.lazy(() => import('./pages/class/ClassForm'));
const ClassList = React.lazy(() => import('./pages/class/ClassList'));
const TeacherList = React.lazy(() => import('./pages/teacher/TeacherList'));
const StudentList = React.lazy(() => import('./pages/student/StudentList'));
const StudentReportCard = React.lazy(() => import('./pages/student/StudentReportCard'));
const StudentPaymentsPage = React.lazy(() => import('./pages/student/StudentPaymentsPage'));
const StudentAttendanceHistory = React.lazy(() => import('./pages/student/StudentAttendanceHistory'));
const SubjectList = React.lazy(() => import('./pages/subject/SubjectList'));
const TeacherPointagePage = React.lazy(() => import('./pages/teacher/TeacherPointagePage'));
const Employees = React.lazy(() => import('./components/Employees'));
const ForgotPassword = React.lazy(() => import('./components/ForgotPassword'));
const TeacherDashboard = React.lazy(() => import('./pages/teacher/TeacherDashboard'));
const TeacherLayout = React.lazy(() => import('./components/TeacherLayout'));
const TeacherLogin = React.lazy(() => import('./TeacherLogin'));
const TeacherAttendancePage = React.lazy(() => import('./pages/teacher/TeacherAttendancePage'));
const ClassesPage = React.lazy(() => import('./pages/teacher/ClassesPage'));
const StudentGradesPage = React.lazy(() => import('./pages/teacher/StudentGradesPage'));
const TeacherBulletinEdit = React.lazy(() => import('./pages/teacher/TeacherBulletinEdit'));
const TeacherChangePassword = React.lazy(() => import('./pages/teacher/TeacherChangePassword'));
const TeacherSchedule = React.lazy(() => import('./pages/teacher/TeacherSchedule'));
const TeacherPointage = React.lazy(() => import('./pages/teacher/TeacherPointage'));
const AdminAttendancePage = React.lazy(() => import('./components/AdminAttendancePage'));
const ExpensesPage = React.lazy(() => import('./components/ExpensesPage'));
const BulletinList = React.lazy(() => import('./pages/bulletin/BulletinList'));
const BulletinEdit = React.lazy(() => import('./pages/bulletin/BulletinEdit'));
const SchoolSetupPage = React.lazy(() => import('./pages/school/SchoolSetupPage'));
const BulletinView = React.lazy(() => import('./pages/bulletin/BulletinView'));
const UserManagement = React.lazy(() => import('./components/admin/UserManagement'));
const SuperAdminLayout = React.lazy(() => import('./components/SuperAdminLayout'));
const SuperAdminDashboard = React.lazy(() => import('./pages/superadmin/SuperAdminDashboard'));
const SuperAdminCodes = React.lazy(() => import('./pages/superadmin/SuperAdminCodes'));
const SuperAdminLeads = React.lazy(() => import('./pages/superadmin/SuperAdminLeads'));
const DemandeDemo = React.lazy(() => import('./pages/DemandeDemo'));



// Affiché le temps de télécharger le morceau de code d'une page (quelques
// dizaines de ko, une seule fois par page et par visite).
const EcranDeChargement = () => (
  <div className="flex min-h-screen items-center justify-center bg-canvas">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-line border-t-brand" />
  </div>
);

function App() {
  return (
    <AuthProvider>
      {/* v7_startTransition : sans ce drapeau, React 18 remplace TOUTE l'interface
          par l'indicateur de chargement au premier accès à une page découpée. */}
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Suspense fallback={<EcranDeChargement />}>
        <Routes>
        <Route path="teacher-login"  element={<TeacherLogin />} />

          {/* Super Admin routes */}
          <Route path="superadmin/*" element={<PrivateRoute><SuperAdminLayout/></PrivateRoute>}>
            <Route index element={<Navigate to="dashboard" replace/>}/>
            <Route path="dashboard" element={<SuperAdminDashboard/>}/>
            <Route path="codes" element={<SuperAdminCodes/>}/>
            <Route path="leads" element={<SuperAdminLeads/>}/>
          </Route>

          <Route path="teacher/*" element={<PrivateRoute requiredRole="teacher"><TeacherLayout/></PrivateRoute>}>
            <Route index element={<Navigate to="dashboard" replace/>}/>
            <Route path="dashboard"  element={<TeacherDashboard/>}/>
             <Route path="attendance" element={<TeacherAttendancePage />}/> 
            <Route path="classes" element={<ClassesPage />}/> 
            <Route path="schedule" element={<TeacherSchedule />}/> 
            <Route path="pointage" element={<TeacherPointage />}/> 
            <Route path="bulletin/:classId" element={<TeacherBulletinEdit />} />
            <Route path="change-password" element={<TeacherChangePassword />} />
            <Route path="student-grades/:studentId" element={<StudentGradesPage />} />
          </Route>
          <Route path="/login" element={<Login />} />
          <Route
            path="/school-setup"
            element={
              <PrivateRoute>
                <SchoolSetupPage />
              </PrivateRoute>
            }
          />
          <Route path="/register" element={<Register />} />
          <Route path="/demande" element={<DemandeDemo />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route element={<Layout />}>
            <Route
              path="/students"
              element={
                <PrivateRoute>
                  <StudentList />
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/classes" element={<ClassList />} />
            <Route path="/classes/create" element={<ClassForm />} />
            <Route path="/classes/edit/:id" element={<ClassForm isEditing={true} />} />
            <Route path="/attendance" element={<AdminAttendancePage />} />
            <Route
              path="/subjects"
              element={
                <PrivateRoute>
                  <SubjectList />
                </PrivateRoute>
              }
            />
            <Route
              path="/teachers"
              element={
                <PrivateRoute>
                  <TeacherList />
                </PrivateRoute>
              }
            />

            <Route
              path="/teachers/:teacherId/pointage"
              element={
                <PrivateRoute>
                  <TeacherPointagePage />
                </PrivateRoute>
              }
            />

        <Route
          path="/employees"
          element={
            <PrivateRoute roles={['admin', 'super admin']}>
              <Employees />
            </PrivateRoute>
          }
        />

            <Route
              path="/statistics"
              element={
                <PrivateRoute>
                  <StatisticsPage />
                </PrivateRoute>
              }
            />

            <Route
              path="/students/:id/report-card"
              element={
                <PrivateRoute>
                  <StudentReportCard />
                </PrivateRoute>
              }
            />

            <Route
              path="/students/:id/payments"
              element={
                <PrivateRoute>
                  <StudentPaymentsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/students/:id/attendance"
              element={
                <PrivateRoute>
                  <StudentAttendanceHistory />
                </PrivateRoute>
              }
            />
            <Route
                path="/calendar"
                element={
                  <PrivateRoute>
                    <SchoolSchedule />
                  </PrivateRoute>
                }
              />
              <Route
              path="/fees"
              element={
                <PrivateRoute>
                  <FeesPage />
                </PrivateRoute>
              }
            />
              <Route
              path="/expenses"
              element={
                <PrivateRoute>
                  <ExpensesPage />
                </PrivateRoute>
              }
            />
            
            {/* Routes Bulletins */}
            <Route
              path="/bulletins"
              element={
                <PrivateRoute>
                  <BulletinList />
                </PrivateRoute>
              }
            />
            <Route
              path="/bulletins/edit/:classeId/:trimestre/:annee"
              element={
                <PrivateRoute>
                  <BulletinEdit />
                </PrivateRoute>
              }
            />
            <Route
              path="/bulletins/view/:bulletinId/:trimester"
              element={
                <PrivateRoute>
                  <BulletinView />
                </PrivateRoute>
              }
            />
              <Route
              path="/settings"
              element={
                <PrivateRoute>
                  <Settings />
                </PrivateRoute>
              }
            />

            {/* Route pour la gestion des utilisateurs - Admin uniquement */}
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute permission="users.read">
                  <UserManagement />
                </ProtectedRoute>
              }
            />
            
          </Route>
        </Routes>
        </Suspense>
      </Router>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnFocusLoss={true}
        draggable
        pauseOnHover
        theme="light"
        limit={3}
      />
    </AuthProvider>
  );
}

export default App;